export const tierOneHeadings = ['h1', 'h2', 'h3']; // headings that are used for the table of contents

export const allowedTags = [
  'address',
  'article',
  'aside',
  'footer',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hgroup',
  'main',
  'nav',
  'section',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'hr',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'ul',
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'br',
  'cite',
  'code',
  'data',
  'dfn',
  'em',
  'i',
  'kbd',
  'mark',
  'q',
  'rb',
  'rp',
  'rt',
  'rtc',
  'ruby',
  's',
  'samp',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'u',
  'var',
  'wbr',
  'caption',
  'col',
  'colgroup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'img',
  'video',
  'source',
  'iframe',
];

export const attributeMap = {
  // Standard HTML attributes requiring camelCase
  acceptcharset: 'acceptCharset',
  accesskey: 'accessKey',
  autocomplete: 'autoComplete',
  autofocus: 'autoFocus',
  autoplay: 'autoPlay', // For <video> and <audio>
  autosave: 'autoSave',
  bgcolor: 'bgColor',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  charset: 'charSet',
  class: 'className',
  classid: 'classID',
  colspan: 'colSpan',
  contenteditable: 'contentEditable',
  contextmenu: 'contextMenu',
  crossorigin: 'crossOrigin',
  datetime: 'dateTime',
  enctype: 'encType',
  for: 'htmlFor',
  formaction: 'formAction',
  formenctype: 'formEncType',
  formmethod: 'formMethod',
  formnovalidate: 'formNoValidate',
  formtarget: 'formTarget',
  frameborder: 'frameBorder',
  hreflang: 'hrefLang',
  inputmode: 'inputMode',
  longdesc: 'longDesc',
  marginheight: 'marginHeight',
  marginwidth: 'marginWidth',
  maxlength: 'maxLength',
  mediagroup: 'mediaGroup',
  minlength: 'minLength',
  novalidate: 'noValidate',
  playsinline: 'playsInline', // For <video> and <audio>
  readonly: 'readOnly',
  rowspan: 'rowSpan',
  spellcheck: 'spellCheck',
  srcdoc: 'srcDoc',
  srclang: 'srcLang',
  srcset: 'srcSet',
  tabindex: 'tabIndex',
  usemap: 'useMap',
  allowfullscreen: 'allowFullScreen',
  allowpaymentrequest: 'allowPaymentRequest',
  allowtransparency: 'allowTransparency',
  allowusermedia: 'allowUserMedia',
  lowsrc: 'lowSrc',
  // SVG-specific attributes requiring camelCase
  clippath: 'clipPath',
  fillopacity: 'fillOpacity',
  fontfamily: 'fontFamily',
  fontsize: 'fontSize',
  fontweight: 'fontWeight',
  gradienttransform: 'gradientTransform',
  gradientunits: 'gradientUnits',
  markerend: 'markerEnd',
  markerheight: 'markerHeight',
  markermid: 'markerMid',
  markerstart: 'markerStart',
  markerunits: 'markerUnits',
  markerwidth: 'markerWidth',
  maskcontentunits: 'maskContentUnits',
  maskunits: 'maskUnits',
  patterncontentunits: 'patternContentUnits',
  patternunits: 'patternUnits',
  preserveaspectratio: 'preserveAspectRatio',
  spreadmethod: 'spreadMethod',
  stopcolor: 'stopColor',
  stopopacity: 'stopOpacity',
  strokeopacity: 'strokeOpacity',
  strokewidth: 'strokeWidth',
  strokelinecap: 'strokeLinecap',
  strokelinejoin: 'strokeLinejoin',
  textanchor: 'textAnchor',
  textdecoration: 'textDecoration',
  viewbox: 'viewBox',
  xlinkactuate: 'xlinkActuate',
  xlinkarcrole: 'xlinkArcrole',
  xlinkhref: 'xlinkHref',
  xlinkrole: 'xlinkRole',
  xlinkshow: 'xlinkShow',
  xlinktitle: 'xlinkTitle',
  xlinktype: 'xlinkType',
  xmlbase: 'xmlBase',
  xmllang: 'xmlLang',
  xmlspace: 'xmlSpace',
};

// Memoize sanitize config to avoid recreation
export const sanitizeConfig = {
  allowedTags,
  allowedAttributes: {
    '*': [
      'class',
      'className',
      'id',
      'style',
      'title',
      'alt',
      'role',
      'data-*',
      'width',
      'height',
      'src',
      'srcset',
      'href',
      'target',
      'rel',
      'type',
      'value',
      'placeholder',
      'name',
      'disabled',
      'readonly',
      'checked',
      'selected',
      'multiple',
      'size',
      'maxlength',
      'minlength',
      'min',
      'max',
      'step',
      'pattern',
      'required',
      'autofocus',
      'autoplay',
      'controls',
      'loop',
      'muted',
      'preload',
      'poster',
      'crossorigin',
      'sizes',
      'loading',
      'decoding',
      'fetchpriority',
    ],
    a: ['href', 'target', 'rel', 'download', 'hreflang', 'type'],
    img: ['src', 'srcset', 'alt', 'width', 'height', 'loading', 'decoding', 'sizes', 'crossorigin'],
    video: ['src', 'poster', 'preload', 'autoplay', 'controls', 'loop', 'muted', 'width', 'height'],
    audio: ['src', 'preload', 'autoplay', 'controls', 'loop', 'muted'],
    source: ['src', 'type', 'media', 'sizes', 'srcset'],
    iframe: ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'sandbox'],
    table: ['cellpadding', 'cellspacing', 'border'],
    th: ['colspan', 'rowspan', 'scope'],
    td: ['colspan', 'rowspan'],
    ol: ['start', 'reversed', 'type'],
    li: ['value'],
    code: ['class', 'className'],
    pre: ['class', 'className'],
    blockquote: ['cite'],
    q: ['cite'],
    time: ['datetime'],
    abbr: ['title'],
    dfn: ['title'],
  },
  allowedStyles: {
    '*': {
      color: [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/],
      'background-color': [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/],
      'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
      'font-size': [/^\d+(?:px|em|%)$/],
      'font-weight': [/^(?:normal|bold|bolder|lighter|[1-9]00)$/],
      'font-style': [/^(?:normal|italic|oblique)$/],
      'text-decoration': [/^(?:none|underline|overline|line-through)$/],
      margin: [/^\d+(?:px|em|%)?(?:\s+\d+(?:px|em|%)?)*$/],
      'margin-left': [/^\d+(?:px|em|%)?$/],
      'margin-right': [/^\d+(?:px|em|%)?$/],
      'margin-top': [/^\d+(?:px|em|%)?$/],
      'margin-bottom': [/^\d+(?:px|em|%)?$/],
      padding: [/^\d+(?:px|em|%)?(?:\s+\d+(?:px|em|%)?)*$/],
      'padding-left': [/^\d+(?:px|em|%)?$/],
      'padding-right': [/^\d+(?:px|em|%)?$/],
      'padding-top': [/^\d+(?:px|em|%)?$/],
      'padding-bottom': [/^\d+(?:px|em|%)?$/],
      width: [/^\d+(?:px|em|%)?$/],
      height: [/^\d+(?:px|em|%)?$/],
      'max-width': [/^\d+(?:px|em|%)?$/],
      'max-height': [/^\d+(?:px|em|%)?$/],
      'min-width': [/^\d+(?:px|em|%)?$/],
      'min-height': [/^\d+(?:px|em|%)?$/],
      display: [/^(?:block|inline|inline-block|none|flex|grid)$/],
      float: [/^(?:left|right|none)$/],
      clear: [/^(?:left|right|both|none)$/],
      position: [/^(?:static|relative|absolute|fixed)$/],
      top: [/^\d+(?:px|em|%)?$/],
      bottom: [/^\d+(?:px|em|%)?$/],
      left: [/^\d+(?:px|em|%)?$/],
      right: [/^\d+(?:px|em|%)?$/],
      cursor: [/^(?:auto|pointer|default|text|help|wait|crosshair|not-allowed)$/],
      border: [/^\d+(?:px)?\s+(?:solid|dashed|dotted)\s+#[0-9a-f]+$/i],
      'border-radius': [/^\d+(?:px|em|%)?$/],
      'border-collapse': [/^(?:separate|collapse)$/],
      'border-style': [/^(?:none|solid|dashed|dotted|double|groove|ridge|inset|outset)$/],
      'border-color': [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/],
      'list-style': [/^(?:none|disc|circle|square|decimal|lower-roman|upper-roman|lower-alpha|upper-alpha)$/],
      'list-style-type': [/^(?:none|disc|circle|square|decimal|lower-roman|upper-roman|lower-alpha|upper-alpha)$/],
    },
  },
  allowVulnerableTags: false,
  // Allow processing of malformed tags to avoid truncation on content with entity-encoded angle brackets
  allowProtocolRelative: true,
  enforceHtmlBoundary: false,
  // Don't discard text content that appears to be malformed tags
  disallowedTagsMode: 'discard',
  transformTags: {
    '*': (tagName, attribs) => {
      const newAttribs = { ...attribs };
      Object.keys(newAttribs).forEach(key => {
        if (attributeMap[key]) {
          newAttribs[attributeMap[key]] = newAttribs[key];
          delete newAttribs[key];
        }
      });
      return { tagName, attribs: newAttribs };
    },
  },
};
